export type ResolutionCapLevel = 0 | 80 | 100

interface CapSituation {
	level: 80 | 100
	trialing?: boolean
	payPerUseEnabled?: boolean
}

// The count the agent stops at, or null when nothing stops it. A trial has no
// card, so it spends its flat allowance and the pay-per-use cap never counts.
export function resolutionCeiling({
	freeLimit,
	trialing,
	payPerUseEnabled,
	monthlyLimit,
}: {
	freeLimit: number
	trialing: boolean
	payPerUseEnabled: boolean
	monthlyLimit: number | null
}): number | null {
	if (trialing || !payPerUseEnabled) return freeLimit
	if (monthlyLimit === null) return null
	return freeLimit + monthlyLimit
}

// A zero ceiling is a plan without the agent, which has nothing to warn about.
export function resolutionCapLevel({
	used,
	ceiling,
}: {
	used: number
	ceiling: number | null
}): ResolutionCapLevel {
	if (ceiling === null || ceiling <= 0) return 0
	if (used >= ceiling) return 100
	if (used >= ceiling * 0.8) return 80
	return 0
}

// The mail subject, its heading and the bell all read this, so they never drift.
export function resolutionCapHeading({
	level,
	trialing = false,
	payPerUseEnabled = false,
}: CapSituation): string {
	const reached = level === 100
	if (trialing) {
		return reached
			? "Your trial's AI resolutions are used up"
			: "You've used 80% of your trial's AI resolutions"
	}
	if (!reached) return "You've used 80% of your AI resolutions"
	return payPerUseEnabled
		? "Your AI agent stopped at its monthly cap"
		: "Your AI agent stopped resolving"
}

// The one action that starts the agent again, named the same way the button on
// the settings page names it.
export function resolutionCapAction({
	trialing = false,
	payPerUseEnabled = false,
}: Omit<CapSituation, "level">): string {
	if (trialing) return "Add payment details"
	return payPerUseEnabled ? "Raise your monthly cap" : "Enable pay-per-use"
}

export function resolutionCapConsequence(level: 80 | 100): string {
	return level === 100
		? "Every customer message now waits for a person."
		: "At 100% every customer message waits for a person instead."
}
